import React, { useState, useEffect } from 'react'
import { withStyles } from '@material-ui/core/styles'
import {
  Typography,
  Box,
  LinearProgress,
  Button,
  TextField,
  Chip,
  IconButton,
} from '@material-ui/core'
import DescriptionIcon from '@material-ui/icons/Description'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import RefreshIcon from '@material-ui/icons/Refresh'
import styles from './styles'

const ProcessStep = ({ classes, processDocuments, onProcessComplete, selectedFolder, selectedFiles = [] }) => {
  const {
    processing,
    progress,
    step,
    result,
    error,
    reconnecting,
    reconnectAttempt,
    startProcessing,
  } = processDocuments
  const [courses, setCourses] = useState([])
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    if (result && result.modules && result.modules.length > 0) {
      const grouped = result.modules.reduce((acc, module) => {
        const title = module.chapter_title || 'Uncategorized'
        if (!acc[title]) {
          acc[title] = {
            courseTitle: title,
            chapters: []
          }
        }
        acc[title].chapters.push(module)
        return acc
      }, {})
      setCourses(Object.values(grouped))
    }
  }, [result])

  const handleRetry = () => {
    if (selectedFolder) {
      const fileIds = selectedFiles.map(f => f.id).filter(Boolean)
      startProcessing(selectedFolder.id, null, fileIds.length > 0 ? fileIds : undefined)
    }
  }

  const handleTitleChange = (index, value) => {
    const updated = [...courses]
    updated[index].courseTitle = value
    setCourses(updated)
  }

  const handleRemoveChapter = (courseIndex, chapterIndex) => {
    const updated = [...courses]
    updated[courseIndex].chapters.splice(chapterIndex, 1)
    setCourses(updated)
  }

  const handleRemoveCourse = (index) => {
    const updated = [...courses]
    updated.splice(index, 1)
    setCourses(updated)
  }

  const handleAddCourse = () => {
    setCourses([...courses, { courseTitle: 'New Course', chapters: [] }])
  }

  const handleContinue = () => {
    setIsAnimating(true)
    setTimeout(() => {
      setIsAnimating(false)
      const organizedCourses = courses
        .filter(course => course.chapters.length > 0)
        .map(course => ({
          courseTitle: course.courseTitle,
          chapters: course.chapters.map(c => ({
            ...c,
            chapter_title: course.courseTitle // Use the (possibly edited) course title
          }))
        }))
      onProcessComplete(organizedCourses)
    }, 350)
  }

  if (error) {
    return (
      <div className={classes.root}>
        <div className={classes.errorContainer}>
          <Typography className={classes.errorText}>
            {error}
          </Typography>
          <Button
            variant="contained"
            className={classes.retryButton}
            onClick={handleRetry}
          >
            Retry Processing
          </Button>
        </div>
      </div>
    )
  }

  if (processing || (!result && !error)) {
    return (
      <div className={classes.root}>
        <div className={classes.processingContainer}>
          <div className={classes.scanner}>
            <DescriptionIcon className={classes.documentIcon} />
          </div>
          <div className={classes.progressInfo}>
            <LinearProgress
              variant="determinate"
              value={progress}
              className={classes.progressBar}
            />
          </div>
          <Typography className={classes.stepText}>
            {reconnecting ? `Reconnecting... (attempt ${reconnectAttempt}/3)` : (step || 'Processing documents...')}
          </Typography>
          {reconnecting && (
            <Box mt={1}>
              <Typography variant="caption" style={{ color: '#DC2626', fontWeight: 600 }}>
                Connection lost. Trying to resume...
              </Typography>
            </Box>
          )}
          <Typography style={{ color: '#94A3B8', fontSize: '13px', marginTop: '8px' }}>
            {progress}% Complete
          </Typography>
        </div>
      </div>
    )
  }

  if (result) {
    return (
      <div className={classes.root}>
        <div className={classes.completeContainer}>
          <Typography className={classes.summaryTitle}>
            Review Course Outline
          </Typography>
          <Typography className={classes.summarySubtitle}>
            We've organized your documents into courses. You can refine the structure below.
          </Typography>

          <div className={classes.courseOutlineContainer}>
            {courses.map((course, idx) => (
              <div key={idx} className={classes.courseCard}>
                <div className={classes.courseHeader}>
                  <TextField
                    className={classes.courseTitleField}
                    value={course.courseTitle}
                    onChange={(e) => handleTitleChange(idx, e.target.value)}
                    placeholder="Course Title"
                    InputProps={{ disableUnderline: false }}
                  />
                  <IconButton
                    className={classes.deleteCourseBtn}
                    onClick={() => handleRemoveCourse(idx)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>

                <div className={classes.attachmentList}>
                  {course.chapters.map((chapter, cIdx) => (
                    <Chip
                      key={`${idx}-${cIdx}`}
                      className={classes.fileChip}
                      label={`${chapter.source_pdf} (pp. ${chapter.page_range?.start || 0}-${chapter.page_range?.end || 0})`}
                      onDelete={() => handleRemoveChapter(idx, cIdx)}
                      onClick={() => {
                        const page = chapter.page_range?.start || 1
                        const url = `${chapter.source_pdf_cdn_url}#page=${page}`
                        window.open(url, '_blank')
                      }}
                    />
                  ))}
                  {course.chapters.length === 0 && (
                    <Typography variant="caption" style={{ color: '#94A3B8', fontStyle: 'italic', marginLeft: '4px' }}>
                      No chapters assigned to this course
                    </Typography>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className={classes.continueButtonContainer}>
            <IconButton
              className={classes.reExtractButton}
              onClick={handleRetry}
              style={{ width: 56, height: 56, borderRadius: '50%', flexShrink: 0, marginRight: 16 }}
            >
              <RefreshIcon style={{ fontSize: 28 }} />
            </IconButton>
            <IconButton
              className={`${classes.continueButton} ${isAnimating ? classes.animating : ''}`}
              onClick={handleContinue}
            >
              <ArrowForwardIcon />
            </IconButton>
          </div>
        </div>
      </div>
    )
  }

  return null
}

export default withStyles(styles)(ProcessStep)
